'use client'

import React, { useRef } from 'react'
import type { SpecificationTableBlock as SpecificationTableBlockProps } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import { LabelCell, TextCell } from '@/components/tables'
import { useDragScroll, useScrollProgress, Scrollbar } from '@/components/ui/horizontal-scroll'
import RichText from '@/components/RichText'

type Props = SpecificationTableBlockProps & {
  disableInnerContainer?: boolean
}

export const SpecificationTableBlock: React.FC<Props> = ({
  title,
  caption,
  tableHeaders,
  rows,
  boldLabels = true,
  tableNote,
  tableNoteAlignment = 'left',
  paddingTop,
  paddingBottom,
}) => {
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  useDragScroll(scrollContainerRef)
  const { scrollProgress, isVisible: showScrollIndicator } = useScrollProgress(scrollContainerRef)

  const headers = tableHeaders || []

  // Auto-calculate number of value columns based on data
  // If headers exist: valueColumns = headers.length - 1 (first header is for label column)
  // Otherwise: valueColumns = max values count across all rows
  const valueColumns =
    headers.length > 0
      ? Math.max(1, headers.length - 1)
      : Math.max(1, ...(rows?.map((row) => row.values?.length || 0) || [1]))

  const hasMultipleColumns = valueColumns > 1

  if (!rows || rows.length === 0) {
    return null
  }

  return (
    <div className={cn('w-full overflow-hidden', paddingClasses)}>
      {title && (
        <h3 className="display-small mb-8">{title}</h3>
      )}

      <div ref={scrollContainerRef} className="w-full overflow-x-auto scrollbar-hide">
        <table
          className="w-full border-separate border-spacing-0 table-fixed"
          style={{ minWidth: `${(valueColumns + 1) * 160}px` }}
        >
          {/* Caption for accessibility */}
          {caption && <caption className="sr-only">{caption}</caption>}

          {/* Header row - only shown for multi-column tables */}
          {hasMultipleColumns && headers.length > 0 && (
            <thead>
              <tr>
                {/* All column headers including label column */}
                {Array.from({ length: valueColumns + 1 }).map((_, index) => {
                  const header = headers[index]
                  const isLastColumn = index === valueColumns

                  return (
                    <th
                      key={header?.id || index}
                      scope="col"
                      className={cn(
                        'text-left align-middle px-3 py-5 break-words',
                        'border-b border-civo-cloud',
                        !isLastColumn && 'border-r border-r-civo-cloud/50',
                      )}
                    >
                      <span className="table-header text-black">{header?.header || ''}</span>
                    </th>
                  )
                })}
              </tr>
            </thead>
          )}

          <tbody>
            {rows.map((row, rowIndex) => {
              const isFirstRow = rowIndex === 0
              const values = row.values || []
              // First body row gets top border only if there's no header
              const needsTopBorder = isFirstRow && !hasMultipleColumns

              return (
                <tr
                  key={row.id || rowIndex}
                  className="group transition-colors duration-200 hover:bg-black/5"
                >
                  {/* Label cell */}
                  <th
                    scope="row"
                    className={cn(
                      'text-left align-top px-3 py-5 break-words',
                      'border-b border-civo-cloud',
                      needsTopBorder && 'border-t border-civo-cloud',
                      'border-r border-r-civo-cloud/50',
                    )}
                  >
                    <LabelCell label={row.label || ''} bold={boldLabels ?? true} />
                  </th>

                  {/* Value cells */}
                  {Array.from({ length: valueColumns }).map((_, colIndex) => {
                    const cellValue = values[colIndex]
                    const isLastColumn = colIndex === valueColumns - 1

                    return (
                      <td
                        key={colIndex}
                        className={cn(
                          'text-left align-top px-3 py-5 break-words',
                          'border-b border-civo-cloud',
                          needsTopBorder && 'border-t border-civo-cloud',
                          !isLastColumn && 'border-r border-r-civo-cloud/50',
                        )}
                      >
                        {cellValue ? (
                          <TextCell primary={cellValue.value || ''} align="left" />
                        ) : (
                          <span className="sr-only">No value</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <Scrollbar scrollProgress={scrollProgress} isVisible={showScrollIndicator} className="mt-4" />

      {/* Table Note */}
      {tableNote && (
        <div
          className={cn(
            'mt-6 w-full body-copy-small text-black/70 [&_strong]:text-black [&_b]:text-black',
            tableNoteAlignment === 'center' && 'text-center',
            tableNoteAlignment === 'right' && 'text-right',
          )}
        >
          <RichText data={tableNote} enableProse={false} enableGutter={false} />
        </div>
      )}
    </div>
  )
}
